export interface ColorTokens {
  bg: { from: string; to: string };       // background gradient stops
  ball: { core: string; halo: string };   // lava blob
  bar: { stroke: string; glow: string };  // heating coil
  hole: { fill: string; rim: string };    // dark pockets
  target: { core: string; halo: string }; // lit target hole
  ui: { text: string; textMuted: string; accent: string; bgChip: string };
  fail: { tint: string };                 // cool shift on fail
}

export interface TypeTokens {
  wordmark: { family: string; weight: number; size: number; letterSpacing: number };
  ui:       { family: string; weight: number; size: number };
  hud:      { family: string; weight: number; size: number };
}

export interface MotionTokens {
  capture: { winMs: number; failMs: number };
  levelUp: { ms: number };
  lifeLoss: { dimMs: number };
  ease:    { standard: string; emphasized: string };
  tokenMachine: {
    leverMs: number;
    tokenStaggerMs: number;
    tokenFlightMs: number;
    tokenFadeMs: number;
    holdAfterMs: number;
    counterPulseMs: number;
    idleBreatheMs: number;
  };
}

export interface PhysicsTokens {
  ball: { gravity: number; drag: number; maxSpeed: number };
}

export interface InputTokens {
  joystick: {
    /** Half-width of the no-motion zone around the touch-down center, in logical px. */
    deadzonePx: number;
    /** Offset at which the speed curves saturate, in logical px. */
    refOffsetPx: number;
    /** Max common-mode (vertical/camera pan) speed. Linear-saturating, so the camera pans at constant speed past refOffsetPx. */
    maxVerticalSpeedPxPerSec: number;
    /** Max differential-mode (tilt) speed per side. Quadratic curve, so tilt ramps aggressively under one-sided pressure. */
    maxTiltSpeedPxPerSec: number;
  };
  touch: {
    /** Multiplier applied to per-pointer drag delta before writing to the anchor. */
    dragScale: number;
    /** Hard cap on |leftAnchor − rightAnchor| in logical px (arcade-flipper-style tilt limit). */
    maxTiltDeltaPx: number;
  };
  keyboard: {
    /** Bar-anchor speed while a keyboard key is held, in logical px/sec. */
    speedPxPerSec: number;
  };
  joystickGui: {
    /** Width of the direction arrow (logical px). */
    arrowWidthPx: number;
    /** Height of the direction arrow (logical px). */
    arrowHeightPx: number;
    /** Gap between the thumb and the arrow tip, in the direction of motion (logical px). */
    arrowOffsetPx: number;
    /** Arrow alpha when shown. */
    arrowAlpha: number;
    /** Fade-in/out duration on direction change / release (ms). */
    fadeMs: number;
  };
}

export interface HoleTokens {
  starfield: {
    /** Number of stars rendered inside each incorrect hole. */
    starsPerHole: number;
    /** Cool-tinted stars (most stars). Hex string. */
    starColorCool: string;
    /** Warm-tinted stars (a minority for variation). Hex string. */
    starColorWarm: string;
    /** Fraction of stars that use the warm tint (0..1). */
    warmFraction: number;
    /** Twinkle frequency in Hz. */
    twinkleHz: number;
    /** Rest-state intensity multiplier (0..1). */
    baseStarBrightness: number;
    /** Distance from ball (logical px) within which holes start to "wake". */
    wakeRadiusPx: number;
    /** Additional star-intensity multiplier at full wake. */
    wakeStarBoost: number;
    /** Rim shimmer strength at full wake. */
    wakeRimShimmerStrength: number;
  };
}

export const tokens = {
  colors: {
    bg:     { from: '#1B0A2E', to: '#3A1245' },
    ball:   { core: '#FF7A2A', halo: '#FFC36B' },
    bar:    { stroke: '#FFB347', glow: '#FF6A1A' },
    hole:   { fill: '#0E0418', rim: '#4A1E5A' },
    target: { core: '#FFE6A8', halo: '#FF8C2E' },
    ui:     { text: '#FFE8D6', textMuted: '#B89BB8', accent: '#FF4DA8', bgChip: '#2A0E3D' },
    fail:   { tint: '#5C8FA8' }
  } satisfies ColorTokens,

  type: {
    wordmark: { family: '"Bungee Inline", "Impact", sans-serif', weight: 400, size: 44, letterSpacing: 2 },
    ui:       { family: '"Familjen Grotesk", system-ui, -apple-system, sans-serif', weight: 600, size: 15 },
    hud:      { family: '"Familjen Grotesk", system-ui, -apple-system, sans-serif', weight: 700, size: 13 }
  } satisfies TypeTokens,

  motion: {
    capture:      { winMs: 350, failMs: 350 },
    levelUp:      { ms: 1200 },
    lifeLoss:     { dimMs: 200 },
    ease:         { standard: 'Sine.easeInOut', emphasized: 'Cubic.easeOut' },
    tokenMachine: {
      leverMs: 240,
      tokenStaggerMs: 120,
      tokenFlightMs: 600,
      tokenFadeMs: 150,
      holdAfterMs: 250,
      counterPulseMs: 180,
      idleBreatheMs: 3000
    }
  } satisfies MotionTokens,

  physics: {
    // 1D rolling sim for the ball on the tilting bar. See
    // docs/superpowers/specs/2026-05-15-ball-physics-design.md.
    // gravity: along-surface acceleration coefficient (px/s²).
    // drag:    linear damping (1/s); e-folding time after bar flattens is 1/drag.
    // maxSpeed: safety cap on |vx| (px/s); only matters at extreme tilts.
    ball: { gravity: 2400, drag: 0.9, maxSpeed: 500 }
  } satisfies PhysicsTokens,

  input: {
    joystick: { deadzonePx: 15, refOffsetPx: 100, maxVerticalSpeedPxPerSec: 500, maxTiltSpeedPxPerSec: 500 },
    touch: { dragScale: 0.6, maxTiltDeltaPx: 160 },
    keyboard: { speedPxPerSec: 120 },
    joystickGui: {
      arrowWidthPx: 18,
      arrowHeightPx: 14,
      arrowOffsetPx: 28,
      arrowAlpha: 0.85,
      fadeMs: 120,
    },
  } satisfies InputTokens,

  holes: {
    starfield: {
      starsPerHole: 8,
      starColorCool: '#A8C9FF',
      starColorWarm: '#FFE0B0',
      warmFraction: 0.3,
      twinkleHz: 0.4,
      baseStarBrightness: 1.0,
      wakeRadiusPx: 80,
      wakeStarBoost: 2.2,
      wakeRimShimmerStrength: 0.45,
    },
  } satisfies HoleTokens,
} as const;

export type Tokens = typeof tokens;
